// Vendor imports
const _ = require('lodash')
const browser = require('bowser')
const qs = require('qs')
// Shared imports
const { parseAndroidClient } = require('shared/utils')

const REFERRAL_REDIRECT_PATH = '/referral/redirect'

const nativeWindowOpen = window.open
const safeWindowNames = ['_parent', '_self', '_top']

function patch() {
  window.open = openWindow
}

function openWindow(url, windowName, windowFeatures) {
  // Patch `window.open` to prevent "Reverse Tabnabbing" attacks
  // https://www.owasp.org/index.php/Reverse_Tabnabbing
  // https://github.com/danielstjules/blankshield#solutions
  if (safeWindowNames.includes(windowName)) {
    return nativeWindowOpen.call(window, url, windowName, windowFeatures)
  }
  // Fix "Blank" page when opening external links on Android app
  if (parseAndroidClient(navigator.userAgent)) {
    return openWindowWithOpenerHack(url, windowName, windowFeatures)
  }
  // Ensure that a referrer is set by bouncing off a redirect endpoint
  const redirectUrl = REFERRAL_REDIRECT_PATH + '?' + qs.stringify({
    source: window.location.pathname + window.location.search,
    destination: url
  })
  if (browser.msie) {
    // IE has issues with the IFRAME hack, but supports the opener hack
    return openWindowWithOpenerHack(redirectUrl, windowName, windowFeatures)
  }
  return openWindowWithIframeHack(redirectUrl, windowName, windowFeatures)
}

function openWindowWithIframeHack(url, windowName, windowFeatures) {
  // Stringify arguments
  const openArgs = [url, windowName, windowFeatures]
  .map(arg => _.isString(arg) ? `"${arg}"` : 'undefined')
  .join(', ')
  // Create an ephemeral hidden IFRAME
  const iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  document.body.appendChild(iframe)
  const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
  // Create a SCRIPT to evaluate inside the IFRAME
  const script = iframeDoc.createElement('script')
  script.type = 'text/javascript'
  script.text = `
    window.parent = null;
    window.top = null;
    window.frameElement = null;
    var child = window.open(${openArgs});
    if (child) { child.opener = null; }
  `
  iframeDoc.body.appendChild(script)
  const { child } = iframe.contentWindow
  document.body.removeChild(iframe)
  return child
}

function openWindowWithOpenerHack(url, windowName, windowFeatures) {
  const child = nativeWindowOpen.call(window, url, windowName, windowFeatures)
  try {
    child.opener = null
  } catch (e) {
    // Do nothing; this happens normally in some browsers
  }
  return child
}

function centeredWindowFeatures(width, height) {
  // Handle multi-screen position
  const multiScreenLeft =
    _.get(window, 'screenLeft',
      _.get(screen, 'left'))
  const multiScreenTop =
    _.get(window, 'screenTop',
      _.get(screen, 'top'))

  const left = (windowWidth() / 2) - (width / 2) + multiScreenLeft
  const top = (windowHeight() / 2) - (height / 2) + multiScreenTop

  return [
    'scrollbars=yes', 'resizable=yes',
    `width=${width}`, `height=${height}`,
    `left=${left}`, `top=${top}`
  ].join(',')
}

function windowWidth() {
  return (
    _.get(window, 'innerHeight') ||
    _.get(document, 'documentElement.clientHeight') ||
    _.get(screen, 'height')
  )
}

function windowHeight() {
  return (
    _.get(window, 'innerHeight') ||
    _.get(document, 'documentElement.clientHeight') ||
    _.get(screen, 'height')
  )
}

function getScrollParent(node) {
  if (!node) {
    return null
  }
  const isElement = node instanceof HTMLElement
  const overflowY = isElement && window.getComputedStyle(node).overflowY
  const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden'
  if (isScrollable && node.scrollHeight >= node.clientHeight) {
    return node
  }
  return getScrollParent(node.parentNode) || document.body
}

function workaroundViewportIssues() {
  if (browser.iphone && browser.safari) {
    const $window = $(window)
    $window.on('load pageshow resize', resetViewportHeight)
  }
  if (browser.ios && browser.safari) {
    // Work-around iOS Universal Links banner scroll issues
    require('client/lib/scroll-troll').preventTouchBodyScrolling()
  }
}

function resetViewportHeight() {
  const { body } = document
  let height = windowHeight()
  body.scrollTop = 0
  body.style.height = height + 'px'
}

module.exports = {
  // Global helpers
  patch,
  getScrollParent,
  workaroundViewportIssues,
  // Opener helpers
  openWindow,
  openWindowWithIframeHack,
  openWindowWithOpenerHack,
  // Feature helpers
  centeredWindowFeatures
}
